import FullCalendar, {
  EventClickArg,
  EventDropArg,
  EventInput,
  DatesSetArg,
} from "@fullcalendar/react"; // must go before plugins
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import useGetJobSchedulesLazy from "hooks/jobs/useGetJobSchedulesLazy";
import { CalendarEvent, CalendarInfo } from "utils/events/transformSchedulesToEvents";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { CustomCrew } from "hooks/crews/useGetAllCrews";
import useGetAllCrews from "hooks/crews/useGetAllCrews";
import Modal from "modules/Modal/Modal";
import ScheduleJobPhase from "modules/jobs/ScheduleJobPhase/ScheduleJobPhase";
import { Job, User } from "generated/graphql";
import { useCalendarContext } from "../JobsSchedule/CalendarContext";
import { useDateRangeContext } from "../JobsSchedule/DateRangeContext";
import QuickBookIcon from "../../../assets/images/quickbooks.svg";
import BilledIcon from "../../../assets/images/billed.svg";
import DollarIcon from "../../../assets/images/dollar.svg";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import useScheduleJobPhaseBlock from "hooks/jobs/useScheduleJobPhaseBlock";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useGetCustomEventBlocksLazy } from "hooks/customEvent/useGetCustomEventBlocksLazy";
import CreateUpdateCustomEvent from "modules/CustomEvent/CreateUpdateCustomEvent";
import ScheduleJob from "modules/jobs/ScheduleJob/ScheduleJob";
import useScheduleCustomEventBlock from "hooks/customEvent/useScheduleCustomEventBlock";
import { JobBillingStatus, UserPermissions } from "generated/graphql";
import { CircularProgress, Menu, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import DispatchDaySummary from "modules/jobs/DispatchDaySummary/DispatchDaySummary";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
dayjs.extend(utc);
dayjs.extend(localizedFormat);

const calendarStyles = `
  .fc {
    width: 100% !important;
  }
  
  .fc-button-group {
    display: inline-flex !important;
    border: none !important;
    border-radius: 6px !important;
    overflow: hidden !important;
  }
  
  .fc-button-group .fc-button {
    background-color: #2c3e50 !important;
    border: 1px solid #2c3e50 !important;
    color: white !important;
    border-radius: 0 !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    padding: 7px 14px !important;
    margin: 0 !important;
    min-width: 60px !important;
    text-transform: none !important;
    border-right: none !important;
  }
  
  .fc-button-group .fc-button:last-child {
    border-right: 1px solid #2c3e50 !important;
  }
  
  .fc-button-group .fc-button:hover {
    background-color: #1e2b37 !important;
  }
  
  .fc-button-group .fc-button-active,
  .fc-button-group .fc-button.fc-button-active {
    background-color: #1a252f !important;
    border: 1px solid #2c3e50 !important;
    border-right: none !important;
    color: white !important;
    font-weight: 400 !important;
  }
  
  .fc-button-group .fc-button-active:last-child {
    border-right: 1px solid #2c3e50 !important;
  }
  
  .fc-button-group .fc-button-active:hover {
    background-color: #1e2b37 !important;
  }
  
  .fc-toolbar {
    border-bottom: 1px solid #e5e7eb !important;
    box-shadow: 1px 2px 4px 1px #e5e7eb !important;
  }

  .fc-toolbar-chunk {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    font-size: 12px !important;
    height: 60px !important;
  }

  .fc-toolbar-title {
    font-size: 1.25rem !important;
  }

  .fc-view-harness-active {
    margin: 0px 10px !important;
    font-size: 12px !important;
  }

  .fc-timegrid-slot {
    height: 40px !important;
  }

  .fc-event-title {
    font-size: 11px !important;
    font-weight: 500 !important;
  }

  .fc-event-time {
    font-size: 10px !important;
  }

  .fc-col-header-cell-cushion {
    font-size: 12px !important;
  }

  .fc-timegrid-slot-label-cushion {
    font-size: 12px !important;
  }

  .fc-dispatch-day-btn {
    border: 1px solid #d0daea;
    background: #f5f8fc;
    border-radius: 6px;
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #2c3e50;
    cursor: pointer;
    margin-left: 6px;
    padding: 0;
    line-height: 1;
  }

  .fc-dispatch-day-btn:hover {
    background: #e8f1ff;
    border-color: #aac3e8;
  }

  .fc-dispatch-day-btn .material-icons {
    font-size: 14px;
  }

  .fc-daygrid-day-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  @media (max-width: 1800px) {
    .fc-toolbar-title {
      font-size: 1rem !important;
      white-space: nowrap !important;
    }
  }

  @media (max-width: 1400px) {
    .fc-toolbar-title {
      font-size: 0.9rem !important;
      white-space: nowrap !important;
    }
    
    .fc-header-toolbar {
      flex-wrap: wrap !important;
      gap: 8px !important;
      row-gap: 12px !important;
    }
    
    .fc-toolbar-chunk {
      flex-wrap: wrap !important;
      gap: 4px !important;
    }
  }

  @media (max-width: 1200px) {
    .fc-toolbar-title {
      font-size: 0.8rem !important;
      white-space: nowrap !important;
    }
    
    .fc-button-group {
      flex-wrap: wrap !important;
    }
    
    .fc-button-group .fc-button {
      font-size: 11px !important;
      padding: 5px 10px !important;
      min-width: 50px !important;
    }
  }

  @media (max-width: 900px) {
    .fc-button-group .fc-button {
      font-size: 10px !important;
      padding: 4px 8px !important;
      min-width: 40px !important;
    }
  }
`;

export default function JobsSchedulesCalendar({
  showCustomEvents = true,
  crewIds,
  jobIds = [],
  projectManagerIds = [],
  compactToolbar = false,
  useViewMenu = false,
  calendarHeight = 1000,
}: {
  crewIds: CustomCrew["id"][];
  jobIds?: Job["id"][];
  showCustomEvents?: boolean;
  projectManagerIds?: User["id"][];
  compactToolbar?: boolean;
  useViewMenu?: boolean;
  calendarHeight?: string | number;
}) {
  const calendarRef = useRef<any>(null);
  const { subscribeCalendarToUpdates, unsubscribeCalendarFromUpdates } = useCalendarContext();
  const { setDateRange } = useDateRangeContext();
  const [getJobSchedulesLazy, { loading }] = useGetJobSchedulesLazy();
  const [getCustomEventBlocksLazy] = useGetCustomEventBlocksLazy();
  const [scheduleJobPhaseBlock, { isSuccess }] = useScheduleJobPhaseBlock();
  const [scheduleCustomEventBlock, { isSuccess: customEventSuccess }] =
    useScheduleCustomEventBlock();
  const eventsRef = useRef({});
  const loadingRef = useRef(false);
  const pendingSuccessCallbacks = useRef<
    Record<string, ((e: CalendarEvent[]) => void)[] | undefined>
  >({});
  const [selectedJobPhaseId, setSelectedJobPhaseId] = useState<string>(null);
  const [selectedCustomEventId, setSelectedCustomEventId] = useState<string>(null);
  const [draggedPhaseData, setDraggedPhaseData] = useState<any>(null);
  const [draggedJobData, setDraggedJobData] = useState<any>(null);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showScheduleJobModal, setShowScheduleJobModal] = useState(false);
  const [selectedDispatchDate, setSelectedDispatchDate] = useState<string | null>(null);
  const [viewMenuAnchorEl, setViewMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [currentViewLabel, setCurrentViewLabel] = useState("Week");
  const { data: crewsData } = useGetAllCrews();
  const { hasPermissions } = useCheckPermissions();
  const { show } = useGlobalMessage();
  const canManageSchedule = hasPermissions(UserPermissions.MANAGE_SCHEDULE);
  const crews = useMemo(() => crewsData || [], [crewsData]);
  const crewsById = useMemo(() => {
    return crews.reduce((acc, crew) => {
      acc[crew.id] = crew;
      return acc;
    }, {} as Record<string, CustomCrew>);
  }, [crews]);

  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = calendarStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Initialize Draggable for external elements
  useEffect(() => {
    if (typeof window !== "undefined") {
      const draggable = new Draggable(document.body, {
        itemSelector: '[data-draggable="true"]',
        eventData: function (eventEl) {
          const phaseData = eventEl.getAttribute("data-phase-data");
          if (phaseData) {
            const data = JSON.parse(phaseData);
            if (data.type === "job") {
              return {
                title: `${data.jobExternalId} - All Phases (${data.phasesCount})`,
                extendedProps: {
                  jobData: data,
                },
              };
            }
            return {
              title: `${data.jobExternalId} - ${data.phaseName}`,
              extendedProps: {
                phaseData: data,
              },
            };
          }
          return null;
        },
      });

      return () => {
        draggable.destroy();
      };
    }
  }, []);

  const handleDatesSet = useCallback(
    (dateInfo: DatesSetArg) => {
      const start = dayjs(dateInfo.start).format("YYYY-MM-DD");
      const end = dayjs(dateInfo.end).subtract(1, "day").format("YYYY-MM-DD");
      setDateRange(start, end);

      const viewLabelMap: Record<string, string> = {
        dayGridMonth: "Month",
        timeGridWeek: "Week",
        timeGridDay: "Day",
        timeGridThreeDay: "3 Day",
        resourceTimelineDay: "Day (Crews)",
        resourceTimelineWeek: "Week (Crews)",
      };
      const nextViewLabel = viewLabelMap[dateInfo.view.type] || "Week";

      setCurrentViewLabel((prevLabel) => {
        return prevLabel === nextViewLabel ? prevLabel : nextViewLabel;
      });
    },
    [setDateRange]
  );

  const handleEventClick = useCallback((block: EventClickArg) => {
    if (block.event.extendedProps?.customEventId) {
      setSelectedCustomEventId(block.event.extendedProps.customEventId);
    } else {
      setSelectedJobPhaseId(block.event.extendedProps.jobPhaseId);
    }
  }, []);

  const handleDrop = useCallback(
    (info: any) => {
      if (!canManageSchedule) {
        show({
          message: "You do not have permission to modify the schedule.",
          options: { variant: "error" },
        });
        return;
      }

      try {
        const dataString = info.draggedEl.getAttribute("data-phase-data");

        if (dataString) {
          const data = JSON.parse(dataString);

          if (data.type === "job") {
            const dropData = {
              ...data,
              dropDate: info.date,
              dropResource: info.resource?.id,
            };
            setDraggedJobData(dropData);
            setShowScheduleJobModal(true);
            return;
          }

          if (data.type === "jobPhase") {
            const dropData = {
              ...data,
              dropDate: info.date,
              dropResource: info.resource?.id,
            };

            setDraggedPhaseData(dropData);
            setShowCreateEventModal(true);
            return;
          }
        }

        const transferData = info.draggedEl.dataset.phaseData;

        if (transferData) {
          const data = JSON.parse(transferData);
          if (data.type === "job") {
            setDraggedJobData({
              ...data,
              dropDate: info.date,
              dropResource: info.resource?.id,
            });
            setShowScheduleJobModal(true);
          } else if (data.type === "jobPhase") {
            setDraggedPhaseData({
              ...data,
              dropDate: info.date,
              dropResource: info.resource?.id,
            });
            setShowCreateEventModal(true);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error parsing dropped data:", error);
      }
    },
    [canManageSchedule, show]
  );

  const handleOpenDaySummary = useCallback((date: Date) => {
    setSelectedDispatchDate(dayjs(date).format("YYYY-MM-DD"));
  }, []);

  const addDayDispatchButton = useCallback(
    (container: Element | null, date: Date) => {
      if (!container) {
        return;
      }

      if (container.querySelector(".fc-dispatch-day-btn")) {
        return;
      }

      const button = document.createElement("button");
      button.type = "button";
      button.className = "fc-dispatch-day-btn";
      button.title = "Open Daily Dispatch Summary";
      button.innerHTML = '<span class="material-icons">fact_check</span>';
      button.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleOpenDaySummary(date);
      };
      container.appendChild(button);
    },
    [handleOpenDaySummary]
  );

  const handleDayHeaderDidMount = useCallback(
    (info: any) => {
      const viewType = info?.view?.type || "";
      if (!(viewType.startsWith("timeGrid") || viewType.startsWith("resourceTimeline"))) {
        return;
      }

      const anchor = info.el?.querySelector(".fc-col-header-cell-cushion");
      addDayDispatchButton(anchor?.parentElement || info.el, info.date);
    },
    [addDayDispatchButton]
  );

  const handleDayCellDidMount = useCallback(
    (info: any) => {
      if (info?.view?.type !== "dayGridMonth") {
        return;
      }

      const anchor = info.el?.querySelector(".fc-daygrid-day-top");
      addDayDispatchButton(anchor, info.date);
    },
    [addDayDispatchButton]
  );

  const handleDropEvent = useCallback(async (event: EventDropArg) => {
    const {
      start,
      end,
      extendedProps: { block },
    } = event.event;
    const hours = dayjs(end).diff(dayjs(start), "h");

    if (event.event.extendedProps.customEventId) {
      const result = await scheduleCustomEventBlock({
        variables: {
          input: {
            blockId: block.id,
            scheduledAt: dayjs.utc(start).format("YYYY-MM-DD HH:mm:ss"),
            blockLength: hours,
          },
        },
      });

      if (!customEventSuccess(result?.data)) {
        event.revert();
      }
    } else {
      const result = await scheduleJobPhaseBlock({
        variables: {
          input: {
            showTravelFrom: block.showTravelFrom,
            showTravelTo: block.showTravelTo,
            travelTimeEstimate: block.travelTime,
            blockId: block.id,
            scheduledAt: dayjs.utc(start).format("YYYY-MM-DD HH:mm:ss"),
            blockLength: hours,
          },
        },
      });

      if (!isSuccess(result?.data)) {
        event.revert();
      }
    }
  }, []);

  const onClose = useCallback(() => {
    setSelectedJobPhaseId(null);
  }, [setSelectedJobPhaseId]);

  const onCloseCustomEvent = useCallback(() => {
    setSelectedCustomEventId(null);
  }, [setSelectedCustomEventId]);

  const onScheduleSuccess = useCallback(() => {
    const calendarApi = calendarRef.current && calendarRef.current.getApi();
    if (calendarApi) {
      calendarApi.refetchEvents();
    }
  }, []);

  useEffect(() => {
    const subscriptionId = subscribeCalendarToUpdates(() => {
      const calendarApi = calendarRef.current && calendarRef.current.getApi();
      if (calendarApi) {
        calendarApi.refetchEvents();
      }
    });

    return () => unsubscribeCalendarFromUpdates(subscriptionId);
  }, []);

  useEffect(() => {
    const calendarApi = calendarRef.current && calendarRef.current.getApi();
    if (calendarApi) {
      const start = dayjs(calendarApi.view.activeStart).format("YYYY-MM-DD");
      const end = dayjs(calendarApi.view.activeEnd).subtract(1, "day").format("YYYY-MM-DD");
      setDateRange(start, end);
    }
  }, [setDateRange]);

  useEffect(() => {
    const calendarApi = calendarRef.current && calendarRef.current.getApi();
    if (calendarApi) {
      calendarApi.refetchEvents();
    }
  }, [crewIds, jobIds, projectManagerIds, showCustomEvents]);

  const fullCalendarEvents = async (
    info: CalendarInfo,
    successCallback: (events: EventInput) => void,
    failureCallback: (error: any) => void
  ) => {
    const dateFrom = dayjs(info.start).format("YYYY-MM-DD HH:mm:ss");
    const dateUntil = dayjs(info.end).format("YYYY-MM-DD HH:mm:ss");

    let customEventFilteredEvents = [];
    if (!loadingRef.current) {
      try {
        loadingRef.current = true;

        const scheduleResult = await getJobSchedulesLazy({
          variables: { dateFrom, dateUntil, jobIds },
          fetchPolicy: "network-only",
        });
        if (showCustomEvents) {
          const customEventResult = await getCustomEventBlocksLazy({
            variables: {
              dateFrom,
              dateUntil,
            },
            fetchPolicy: "network-only",
          });
          customEventFilteredEvents =
            customEventResult?.events?.filter((event) => {
              if (event.crewId) {
                return crewIds.length === 0 || crewIds.includes(event.crewId);
              }
              return true;
            }) || [];
        }

        const scheduledFilteredEvents = scheduleResult.events.filter((event) => {
          const matchesCrew =
            crewIds.length === 0 || crewIds.includes(event.crewId) || !event.crewId;
          const matchesProjectManager =
            projectManagerIds.length === 0 || projectManagerIds.includes(event.userId);
          return matchesCrew && matchesProjectManager;
        });

        const scheduledEventsWithResource = scheduledFilteredEvents.map((event) => {
          if (event.crewId) {
            return {
              ...event,
              resourceId: event.crewId,
            };
          }
          return event;
        });

        const customEventsWithResource = customEventFilteredEvents.map((event) => {
          if (event.crewId) {
            return {
              ...event,
              resourceId: event.crewId,
            };
          }
          return event;
        });

        eventsRef.current[`${dateFrom}${dateUntil}`] = [
          ...scheduledEventsWithResource,
          ...customEventsWithResource,
        ];

        successCallback(eventsRef.current[`${dateFrom}${dateUntil}`]);

        let callback = pendingSuccessCallbacks.current[`${dateFrom}${dateUntil}`]?.pop();

        if (callback) {
          callback(eventsRef.current[`${dateFrom}${dateUntil}`]);
        }
      } catch (error) {
        eventsRef.current[`${dateFrom}${dateUntil}`] = [];
        failureCallback(error);
      } finally {
        loadingRef.current = false;
      }
    } else {
      pendingSuccessCallbacks.current[`${dateFrom}${dateUntil}`] = [
        ...(pendingSuccessCallbacks.current[`${dateFrom}${dateUntil}`] ?? []),
        successCallback,
      ];
    }
  };

  const memoizedFullCalendarEvents = useCallback(
    (info: CalendarInfo, successCallback, failureCallback) => {
      fullCalendarEvents(info, successCallback, failureCallback);
    },
    [crewIds, getCustomEventBlocksLazy, getJobSchedulesLazy, jobIds, projectManagerIds, showCustomEvents]
  );

  const rightToolbarButtons = compactToolbar
    ? useViewMenu
      ? ""
      : "dayGridMonth,timeGridWeek,timeGridDay,timeGridThreeDay"
    : "dayGridMonth,timeGridWeek,timeGridDay,timeGridThreeDay,resourceTimelineDay,resourceTimelineWeek";

  const viewMenuOptions = useMemo(
    () => [
      { value: "dayGridMonth", label: "Month" },
      { value: "timeGridWeek", label: "Week" },
      { value: "timeGridDay", label: "Day" },
      { value: "timeGridThreeDay", label: "3 Day" },
      { value: "resourceTimelineDay", label: "Day (Crews)" },
      { value: "resourceTimelineWeek", label: "Week (Crews)" },
    ],
    []
  );

  const crewsWithEvents = useMemo(() => {
    const allEvents = Object.values(eventsRef.current).flat() as CalendarEvent[];
    const uniqueCrewIds = new Set<string>();

    allEvents.forEach((event) => {
      if (event.crewId) {
        uniqueCrewIds.add(event.crewId);
      }
    });

    return Array.from(uniqueCrewIds);
  }, [eventsRef.current]);

  const resolvedCalendarHeight = calendarHeight === "auto" ? "auto" : calendarHeight;
  const fullCalendarHeight = calendarHeight === "auto" ? "auto" : "100%";

  return (
    <>
      {loading && (
        <MDBox textAlign="center" display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </MDBox>
      )}
      <MDBox
        sx={{
          opacity: loading ? 0 : 100,
          width: "100%",
          height: resolvedCalendarHeight,
          ...(compactToolbar
            ? {
                "& .fc-toolbar": {
                  minHeight: "52px",
                },
                "& .fc-toolbar-chunk:first-of-type": {
                  paddingLeft: "10px",
                },
                "& .fc-toolbar-chunk:last-of-type": {
                  paddingRight: "10px",
                },
                "& .fc-toolbar-chunk": {
                  gap: "6px!important",
                  height: "52px!important",
                },
                "& .fc-button-group .fc-button": {
                  fontSize: "10px!important",
                  padding: "3px 7px!important",
                  minWidth: "30px!important",
                },
                "& .fc-prev-button, & .fc-next-button": {
                  minWidth: "26px!important",
                  padding: "3px 5px!important",
                },
                "& .fc-prev-button .fc-icon, & .fc-next-button .fc-icon": {
                  fontSize: "10px!important",
                },
                "& .fc-today-button": {
                  fontSize: "9px!important",
                  padding: "3px 6px!important",
                  minWidth: "42px!important",
                },
                "& .fc-toolbar-title": {
                  fontSize: "0.9rem!important",
                },
                "& .fc-viewMenu-button": {
                  backgroundColor: "#2c3e50!important",
                  border: "1px solid #2c3e50!important",
                  color: "white!important",
                  borderRadius: "8px!important",
                  fontSize: "0.8rem!important",
                  padding: "4px 10px!important",
                  minWidth: "72px!important",
                  textTransform: "none!important",
                  boxShadow: "none!important",
                },
                "& .fc-viewMenu-button:hover": {
                  backgroundColor: "#1e2b37!important",
                },
              }
            : {}),
        }}
      >
        <FullCalendar
          buttonText={
            compactToolbar
              ? {
                  today: "Today",
                  month: "M",
                  week: "W",
                  day: "D",
                }
              : undefined
          }
          allDaySlot={false}
          droppable={canManageSchedule}
          ref={calendarRef as any}
          timeZone="local"
          editable={canManageSchedule}
          initialView="timeGridWeek"
          eventTimeFormat={{ hour: "numeric", minute: "2-digit" }}
          eventClick={handleEventClick}
          eventDrop={handleDropEvent}
          drop={handleDrop}
          eventReceive={(info) => {
            info.event.remove();

            const phaseData = info.event.extendedProps?.phaseData;
            const jobData = info.event.extendedProps?.jobData;

            if (jobData && jobData.type === "job") {
              setDraggedJobData({
                ...jobData,
                dropDate: info.event.start,
                dropResource: info.event.getResources()?.[0]?.id || null,
              });
              setShowScheduleJobModal(true);
            } else if (phaseData && phaseData.type === "jobPhase") {
              setDraggedPhaseData({
                ...phaseData,
                dropDate: info.event.start,
                dropResource: info.event.getResources()?.[0]?.id || null,
              });
              setShowCreateEventModal(true);
            }
          }}
          datesSet={handleDatesSet}
          slotDuration={"01:00:00"}
          snapDuration={"01:00:00"}
          eventDisplay="block"
          events={memoizedFullCalendarEvents}
          dayHeaderDidMount={handleDayHeaderDidMount}
          dayCellDidMount={handleDayCellDidMount}
          customButtons={
            useViewMenu
              ? {
                  viewMenu: {
                    text: currentViewLabel,
                    click: (_mouseEvent, htmlElement) => {
                      setViewMenuAnchorEl(htmlElement);
                    },
                  },
                }
              : undefined
          }
          eventContent={function (arg) {
            let icon = document.createElement("img");
            let timeText = document.createElement("p");
            let body = document.createElement("div");
            let checklistRatio = document.createElement("div");

            body.innerHTML =
              arg.view.type === "dayGridMonth"
                ? arg.event.title.split(",").shift()
                : arg.event.title;
            timeText.innerHTML = arg.timeText;
            timeText.style.paddingTop = "18px";

            icon.src = QuickBookIcon;
            icon.style.textAlign = "right";
            icon.style.position = "absolute";
            icon.style.right = "0";
            icon.width = 16;
            timeText.style.fontSize = "9px";
            if (arg.event.backgroundColor !== "#000000") {
              icon.style.filter = "invert(1)";
            }
            if (!arg.event.extendedProps?.pushedToQuickBook) {
              icon.style.display = "none";
            }

            if (
              arg.event.extendedProps?.billingStatus == JobBillingStatus.PARTIAL_BILLED ||
              arg.event.extendedProps?.billingStatus == JobBillingStatus.DEPOSIT_BILLED ||
              arg.event.extendedProps?.billingStatus == JobBillingStatus.BILLED
            ) {
              icon.src = BilledIcon;
              icon.style.display = "";
            }
            if (arg.event.extendedProps?.billingStatus == JobBillingStatus.PAID) {
              icon.src = DollarIcon;
              icon.style.display = "";
            }

            if (arg.event.extendedProps?.checklistCompletion) {
              const { ratio, allCompleted } = arg.event.extendedProps.checklistCompletion;

              const isIconVisible =
                arg.event.extendedProps?.pushedToQuickBook ||
                arg.event.extendedProps?.billingStatus == JobBillingStatus.PARTIAL_BILLED ||
                arg.event.extendedProps?.billingStatus == JobBillingStatus.DEPOSIT_BILLED ||
                arg.event.extendedProps?.billingStatus == JobBillingStatus.BILLED ||
                arg.event.extendedProps?.billingStatus == JobBillingStatus.PAID;

              checklistRatio.innerHTML = ratio;
              checklistRatio.style.position = "absolute";
              checklistRatio.style.top = "2px";
              checklistRatio.style.right = isIconVisible ? "24px" : "2px";
              checklistRatio.style.padding = "1px 3px";
              checklistRatio.style.borderRadius = "3px";
              checklistRatio.style.fontSize = "9px";
              checklistRatio.style.fontWeight = "bold";
              checklistRatio.style.color = "white";
              checklistRatio.style.backgroundColor = allCompleted ? "#4CAF50" : "#F44336";
              checklistRatio.style.zIndex = "1";
              checklistRatio.style.opacity = "0.85";
            } else {
              checklistRatio.style.display = "none";
            }

            let arrayOfDomNodes = [icon, timeText, body, checklistRatio];
            return {
              domNodes: arrayOfDomNodes,
            };
          }}
          headerToolbar={{
            left: "prev,next,today",
            center: "title",
            right: useViewMenu ? "viewMenu" : rightToolbarButtons,
          }}
          views={{
            timeGridThreeDay: {
              type: "timeGrid",
              duration: { days: 3 },
              buttonText: compactToolbar ? "3D" : "3 day",
            },
            resourceTimelineDay: {
              type: "resourceTimeline",
              duration: { days: 1 },
              buttonText: compactToolbar ? "Crew D" : "day (crews)",
            },
            resourceTimelineWeek: {
              type: "resourceTimeline",
              duration: { days: 7 },
              buttonText: compactToolbar ? "Crew W" : "week (crews)",
              slotDuration: { days: 1 },
              slotLabelFormat: [{ weekday: "short", day: "numeric", month: "numeric" }],
            },
          }}
          resources={crewIds
            .filter((id) => crewsWithEvents.length === 0 || crewsWithEvents.includes(id))
            .map((id) => ({
              id,
              title: crewsById[id]?.name || `Crew ${id}`,
              eventColor: "#000000",
            }))}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, resourceTimelinePlugin]}
          height={fullCalendarHeight}
          schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
        />
        {useViewMenu && (
          <>
            <Menu
              anchorEl={viewMenuAnchorEl}
              open={!!viewMenuAnchorEl}
              onClose={() => setViewMenuAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {viewMenuOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={option.label === currentViewLabel}
                  onClick={() => {
                    calendarRef.current?.getApi()?.changeView(option.value);
                    setViewMenuAnchorEl(null);
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </MDBox>

      <Modal open={!!selectedJobPhaseId} onClose={onClose}>
        <ScheduleJobPhase
          id={selectedJobPhaseId}
          onClose={onClose}
          onScheduleSuccess={onScheduleSuccess}
        />
      </Modal>
      <Modal open={!!selectedCustomEventId} onClose={onCloseCustomEvent}>
        <CreateUpdateCustomEvent
          id={selectedCustomEventId}
          action="update"
          onClose={onCloseCustomEvent}
          onSuccess={onScheduleSuccess}
        />
      </Modal>

      <Modal
        open={showCreateEventModal && draggedPhaseData?.type === "jobPhase"}
        onClose={() => {
          setShowCreateEventModal(false);
          setDraggedPhaseData(null);
        }}
      >
        {draggedPhaseData?.phaseId && (
          <ScheduleJobPhase
            id={draggedPhaseData.phaseId}
            onClose={() => {
              setShowCreateEventModal(false);
              setDraggedPhaseData(null);
            }}
            onScheduleSuccess={() => {
              onScheduleSuccess();
              setShowCreateEventModal(false);
              setDraggedPhaseData(null);
            }}
            initialScheduledAt={draggedPhaseData.dropDate}
            initialDuration={draggedPhaseData.duration}
          />
        )}
      </Modal>

      <Modal
        open={showScheduleJobModal && draggedJobData?.type === "job"}
        onClose={() => {
          setShowScheduleJobModal(false);
          setDraggedJobData(null);
        }}
        styles={{
          width: "auto",
          height: "auto",
          maxWidth: "950px",
          maxHeight: "90vh",
        }}
      >
        {draggedJobData?.jobId && (
          <ScheduleJob
            jobId={draggedJobData.jobId}
            onClose={() => {
              setShowScheduleJobModal(false);
              setDraggedJobData(null);
            }}
            onScheduleSuccess={() => {
              onScheduleSuccess();
              setShowScheduleJobModal(false);
              setDraggedJobData(null);
            }}
            initialScheduledAt={draggedJobData.dropDate}
            initialDuration={draggedJobData.duration}
          />
        )}
      </Modal>

      <Modal
        open={!!selectedDispatchDate}
        onClose={() => setSelectedDispatchDate(null)}
        styles={{
          width: "95vw",
          height: "92vh",
          maxWidth: "1500px",
          maxHeight: "95vh",
        }}
      >
        {selectedDispatchDate ? (
          <DispatchDaySummary
            date={selectedDispatchDate}
            onClose={() => setSelectedDispatchDate(null)}
          />
        ) : null}
      </Modal>
    </>
  );
}
